(function () {
  "use strict";

  if (customElements.get("sticky-image-cards")) return;

  customElements.define(
    "sticky-image-cards",
    class extends HTMLElement {
      constructor() {
        super();

        this.isDesktop =
          window.theme.getWindowWidth() >= window.theme.sizes.large;
        this.isMobile = window.theme.isMobile();
        this.orientation = window.theme.getScreenOrientation();
        this.isAutoScrolling = false;
        this.autoScrollTarget = null;

        this.onScrollCallback = () => this.onScroll();
        this.onResizeCallback = () => this.onResize();
      }

      connectedCallback() {
        this.container = this.closest(
          '[data-section-type="sticky-image-cards"]'
        );
        this.body = this.querySelector("[data-sticky-card-body]");
        this.wrapper = this.querySelectorAll("[data-sticky-card-wrapper]");
        this.content = this.querySelector("[data-sticky-card-content]");
        this.aside = this.querySelector("[data-sticky-card-aside]");
        this.media = this.querySelectorAll("[data-sticky-card-media]");
        this.textInContent =
          this.content?.querySelectorAll("[data-sticky-card-text]");
        this.vh = this.querySelector("[data-sticky-vh]");
        this.layoutMobile = this.getAttribute(
          "data-sticky-card-layout-mobile"
        );
        this.progressDots = this.container.querySelectorAll(
          "[data-progress-dot]"
        );
        this.highlightInner = this.querySelectorAll('.js-highlight-inner');

        if (this.media.length === 0) return;

        requestAnimationFrame(() => {
          this.calculateHeights();
          this.onScroll();
        });

        if (document.fonts) {
          if (document.fonts.status === "loaded") {
            this.calculateHighlightWidths();
          } else {
            document.fonts.ready.then(() => this.calculateHighlightWidths());
          }
        } else {
          setTimeout(() => this.calculateHighlightWidths(), 100);
        }

        this.progressDots.forEach(dot => {
          dot.addEventListener("click", () => {
            const index = Number(dot.dataset.index);

            this.isAutoScrolling = true;
            this.autoScrollTarget = index;

            this.setActiveDot(index);

            this.scrollToCard(index);
          });
        });

        document.addEventListener("theme:scroll", this.onScrollCallback);
        document.addEventListener("theme:resize", this.onResizeCallback);
      }

      scrollToCard(index) {
        const { stickyHeaderHeight } = window.theme.readHeights();
        const headerHeight = stickyHeaderHeight || 0;

        const parentOffset = Math.round(
          this.body.getBoundingClientRect().top + window.scrollY
        );

        const heights = [...this.wrapper].map((el, i) =>
          i >= index ? 0 : el.offsetHeight
        );

        const cumulativeHeight = heights.reduce((a, b) => a + b, 0);

        let distance =
          parentOffset + cumulativeHeight - this.stickyTop - headerHeight;

        if (index === this.lastIndex) {
          // For some reason clicking the last button doesn't scroll all the way, so we need to add a pixel.
          distance += 1;
        }

        window.scrollTo({
          top: distance,
          behavior: "smooth"
        });

        const targetY = distance;

        const checkIfReached = () => {
          const currentY = window.scrollY;
          if (Math.abs(currentY - targetY) < 2) {
            this.isAutoScrolling = false;
            this.autoScrollTarget = null;
          } else {
            requestAnimationFrame(checkIfReached);
          }
        };

        requestAnimationFrame(checkIfReached);
      }

      onScroll() {
        if (this.isEligible()) {
          this.detectStickyPositions();
        }
      }

      isEligible() {
        const containerTop = Math.round(
          this.container.getBoundingClientRect().top + window.scrollY
        );

        const containerBottom =
          containerTop + this.container.offsetHeight;

        const hasPassed = containerBottom < Math.round(window.scrollY);

        return (
          containerTop <
            Math.round(window.scrollY + window.innerHeight) &&
          !hasPassed
        );
      }

      detectStickyPositions() {
        const bodyTop = Math.round(
          this.body.getBoundingClientRect().top -
            this.headerHeight -
            this.wrapperTop
        );

        [...this.wrapper].forEach((wrapper, index) => {
          const stickyTop = Math.floor(
            wrapper.getBoundingClientRect().top -
              this.headerHeight
          );

          if (this.isMobile) {
            if (index !== this.lastIndex) {
              const nextTop = Math.floor(
                this.wrapper[index + 1].getBoundingClientRect().top -
                  this.headerHeight
              );

              wrapper.classList.toggle(
                "has-passed-sticky",
                nextTop <= this.stickyTop
              );
            }

            this.container.classList.toggle(
              `checkpoint-${index + 1}`,
              bodyTop <= -1 * this.cumulativeHeights[index]
            );
          }

          wrapper.classList.toggle(
            "is-stuck-up",
            stickyTop <= this.stickyTop
          );
        });

        this.updateActiveDot();
      }

      updateActiveDot() {
        if (this.isAutoScrolling) {
          this.setActiveDot(this.autoScrollTarget);
          return;
        }

        let activeIndex = 0;

        this.wrapper.forEach((wrapper, index) => {
          if (wrapper.classList.contains("is-stuck-up")) {
            activeIndex = index;
          }
        });

        if (this.isMobile) {
          this.wrapper.forEach((_, index) => {
            if (this.container.classList.contains(`checkpoint-${index + 1}`)) {
              activeIndex = index;
            }
          });
        }

        this.setActiveDot(activeIndex);
      }

      setActiveDot(index) {
        if (!this.progressDots) return;

        this.progressDots.forEach(dot => {
          const isActive = Number(dot.dataset.index) === index;
          dot.classList.toggle("is-active", isActive);
          dot.setAttribute("aria-current", isActive ? "true" : "false");
        });

        this.updateSlideAccessibility(index);
      }

      updateSlideAccessibility(activeIndex) {
        const updateGroup = (elements) => {
          if (!elements) return;

          elements.forEach((el, index) => {
            const isActive = index === activeIndex;
            el.setAttribute('aria-hidden', String(!isActive));

            const focusable = el.querySelectorAll('a, button, input, select, textarea, [tabindex]');
            focusable.forEach(child => {
              if (isActive) {
                child.removeAttribute('tabindex');
              } else {
                child.setAttribute('tabindex', '-1');
              }
            });
          });
        };

        updateGroup(this.wrapper);
        updateGroup(this.textInContent);
      }

      calculateHighlightWidths() {
        if (this.highlightObserver) {
          this.highlightObserver.disconnect();
        }

        const reset = (inner) => {
          const parent = inner.closest('.sticky-text__highlight');
          if (!parent) return;
          parent.style.width = 'auto';
        }

        const measure = (inner) => {
          const parent = inner.closest('.sticky-text__highlight');
          if (!parent) return;

          const styles = window.getComputedStyle(parent);
          const paddingLeft = parseFloat(styles.paddingLeft) || 0;
          const paddingRight = parseFloat(styles.paddingRight) || 0;

          const width = inner.getBoundingClientRect().width;

          parent.style.width = `${width + paddingLeft + paddingRight}px`;
        };

        this.highlightInner.forEach(inner => {
          reset(inner);
          measure(inner);
        });

        this.highlightObserver = new ResizeObserver(entries => {
          entries.forEach(entry => {
            reset(entry.target);
            measure(entry.target);
          });
        });

        this.highlightInner.forEach(inner => {
          this.highlightObserver.observe(inner);
        });
      }


      calculateHeights() {
        const { stickyHeaderHeight } = window.theme.readHeights();

        this.headerHeight = stickyHeaderHeight || 0;

        this.spacingTop =
          parseInt(
            window
              .getComputedStyle(this.container)
              .getPropertyValue("--padding")
          ) || 20;

        this.wrapperTop =
          parseInt(
            window
              .getComputedStyle(this.wrapper[0])
              .getPropertyValue("top")
          ) || 0;

        this.stickyTop = Math.floor(
          this.wrapperTop - this.headerHeight
        );

        this.gap =
          parseInt(
            window
              .getComputedStyle(this.aside)
              .getPropertyValue("gap")
          ) || 0;

        this.viewportHeight = this.vh.offsetHeight;
        this.lastIndex = [...this.wrapper].length - 1;
        this.cumulativeHeights = [];

        if (this.isMobile) {
          this.wrapperTop = this.spacingTop;
          this.stickyTop = this.spacingTop;
        }

        // Match text + media heights on desktop
        if (this.textInContent && this.isDesktop) {
          this.textInContent.forEach((text, index) => {
            text.style.setProperty("--row-height", "auto");
            this.wrapper[index].style.setProperty(
              "--row-height",
              "auto"
            );

            const textHeight = text.offsetHeight;
            const mediaHeight = this.media[index].offsetHeight;
            const maxHeight = Math.max(textHeight, mediaHeight);

            text.style.setProperty(
              "--row-height",
              `${maxHeight}px`
            );
            this.wrapper[index].style.setProperty(
              "--row-height",
              `${maxHeight}px`
            );
          });
        }

        [...this.wrapper].forEach((wrapper, index) => {
          wrapper.style.setProperty("--item-height", "auto");
          wrapper.style.setProperty("--item-double-height", "auto");

          requestAnimationFrame(() => {
            const height = wrapper.offsetHeight;

            const spillsOver =
              height +
                this.wrapperTop +
                this.headerHeight >
              this.viewportHeight;

            const previousHeights = [...this.wrapper].map(
              (w) =>
                Number(w.dataset.index) >= index
                  ? 0
                  : w.offsetHeight
            );

            const cumulative = previousHeights.reduce(
              (sum, value) => sum + value,
              0
            );

            this.cumulativeHeights.push(cumulative);

            wrapper.classList.toggle("spillover", spillsOver);

            if (spillsOver && this.isMobile) {
              wrapper.style.setProperty(
                "--item-height",
                `${height}px`
              );
              wrapper.style.setProperty(
                "--item-double-height",
                `${2 * height}px`
              );
            }
          });
        });
      }

      onResize() {
        requestAnimationFrame(() => {
          this.isDesktop =
            window.theme.getWindowWidth() >=
            window.theme.sizes.large;

          this.isMobile = window.theme.isMobile();

          const isIOS = /iPhone|iPad|iPod/i.test(
            navigator.userAgent
          );

          let shouldRecalculate =
            this.isDesktop || (!isIOS && this.isMobile);

          if (
            this.orientation !==
              window.theme.getScreenOrientation() &&
            isIOS
          ) {
            shouldRecalculate = true;
            this.orientation =
              window.theme.getScreenOrientation();
          }

          if (shouldRecalculate) {
            this.calculateHeights();
          }

          this.calculateHighlightWidths();
        });
      }

      disconnectedCallback() {
        document.removeEventListener(
          "theme:scroll",
          this.onScrollCallback
        );

        document.removeEventListener(
          "theme:resize",
          this.onResizeCallback
        );
      }
    }
  );
})();
